// components/TipsDetails.jsx
'use client'; // This component is a client-side component

import React from 'react';
import Image from 'next/image';
import Link from "next/link";
import {API_BLOG_POST_ATTACHMENT_DOWNLOAD, API_BLOG_POST_ATTACHMENT_VIEW} from "@constants/next-api-endpoints";
import {formatFileSize} from "@utils/file";
import {LINK_BLOG_POSTS} from "@constants/page-links";
import {LINK_TIP_DETAILS, LINK_TIPS_LIST} from "@constants/page-links/tips";

/**
 * Renders the detailed view of a blog post using Bootstrap 5.3 classes.
 * @param {object} props - The component props.
 * @param {object} props.post - The blog post data.
 */
const TipsDetails = ({post}) => {
   
   // Check if post data is available before rendering
   if (!post) {
      return <div className="p-4 text-center text-secondary">Post not found.</div>;
   }
   
   // Destructure relevant fields from the post object
   const {title, content, author, created_at_formatted, categories, featured_image, attachments} = post;
   
   // Function to render HTML content (use with caution against XSS)
   const renderContent = () => {
      return {__html: content};
   };
   
   return (
      // Use Bootstrap container classes for layout and card for styling
      <div className="container py-4 my-4">
         <div className="card shadow-lg mx-auto" style={{maxWidth: '850px'}}>
            <div className="card-body p-4 p-md-5">
               
               {/* 🖼️ Featured Image Section */}
               {featured_image && featured_image.path_absolute && (
                  <div
                     // Using relative positioning and fixed height for the Image container
                     style={{position: 'relative', height: '400px'}}
                     className="mb-4 rounded overflow-hidden"
                  >
                     {/* Note: The 'fill' prop is used here, so the parent div needs relative positioning and a fixed height. */}
                     <Image
                        src={featured_image.path_absolute}
                        alt={title || "Featured image"}
                        fill
                        style={{objectFit: 'contain', objectPosition: 'center'}}
                        priority // Load the image with high priority
                        className="rounded-3"
                     />
                  </div>
               )}
               
               {/* 📰 Title Section */}
               <h1 className="h1 fw-bold text-dark mb-3 pb-2 border-bottom">
                  {title}
               </h1>
               
               {/* 👤 Author and Date Section */}
               <div className="d-flex align-items-center text-muted small mb-4">
                  {author && (
                     <p className="fw-medium mb-0 me-3">
                        By <span className="text-primary">
                        <Link href={LINK_TIPS_LIST + `?author=${author.author_id}`}>{author.full_name}</Link>
                     </span>
                     </p>
                  )}
                  <span className="text-secondary">•</span>
                  <p className="mb-0 ms-3">
                     <strong>Published</strong> <time dateTime={post.created_at}>{post.created_at_diff}</time>
                  </p>
               </div>
               
               {/* 📜 Content Section (Using dangerouslySetInnerHTML) */}
               <section className="mb-5">
                  {/* Bootstrap 5 doesn't have a direct equivalent to Tailwind's 'prose',
                but using the .lead class and standard HTML structure works well. */}
                  <div className="text-dark" dangerouslySetInnerHTML={renderContent()} />
               </section>
               
               {/* 🏷️ Categories Section */}
               {categories && categories.length > 0 && (
                  <div className="border-top pt-4 mt-4">
                     <h3 className="h5 fw-semibold text-dark mb-3">Categories</h3>
                     <div className="d-flex flex-wrap gap-2">
                        {categories?.map((category) => (
                           <Link
                              key={category.category_id}
                              // Using Bootstrap badge classes for a tag look
                              className="badge bg-success text-white text-decoration-none px-3 py-2 fw-normal"
                            href={LINK_TIPS_LIST + `?categories=${category.category_id}`}>
                    {category.category_name}
                  </Link>
                        ))}
                     </div>
                  </div>
               )}
               
               {/* 📎 Attachments List Section */}
               
               {Array.isArray(attachments) && attachments.length > 0 && (
                  <div className="border-top pt-4 mt-4">
                     <h3 className="h5 fw-semibold text-dark mb-3">Attachments</h3>
                     {attachments.length > 0 ? (
                        <ul className="list-unstyled mb-0">
                           
                           {attachments?.map(
                              (file,index) => (
                                 <li className="mb-1" key={index}>
                                    <i className="fa fa-paperclip me-2 text-primary"></i> {/* Requires Bootstrap Icons */}
                                    
                                    {file.file_name} ({formatFileSize(file.size)})
                                    &mdash;
                                    <Link
                                       href={API_BLOG_POST_ATTACHMENT_VIEW + file.attachment_id}
                                       target="_blank"
                                       rel="noopener noreferrer"
                                       className="text-decoration-none ms-2 me-2 text-primary hover-link"
                                    >
                                       <i className="fa fa-eye"></i> View
                                    </Link>
                                    &mdash;
                                    <Link
                                       href={API_BLOG_POST_ATTACHMENT_DOWNLOAD + file.attachment_id}
                                       target="_blank"
                                       rel="noopener noreferrer"
                                       className="ms-2 ms-2 text-decoration-none text-primary hover-link"
                                    >
                                       <i className="fa fa-download"></i> Download
                                    </Link>
                                 </li>
                              )
                           )}
                        
                        </ul>
                     ) : (
                        <p className="text-secondary">No attachments found.</p>
                     )}
                  </div>
               )}
               
               
            </div>
         </div>
         
         {/*<pre style={{wordBreak: 'break-all', whiteSpace: 'pre-wrap'}}>
            {JSON.stringify(post, null, 2)}
         </pre>*/}
         
      </div>
   );
};

export default TipsDetails;